import { FormattedMessage } from 'react-intl';

import { Button } from '@/components/common/Button';
import Loading from '@/components/common/Loading';
import { useTranslate } from '@/hooks/useTranslate';
import { isCnArea, isFWebSite } from '@/utils/envHelper';
import { EventNewID } from '@/utils/eventlog/eventNewConfig';
import { trackEvent } from '@/utils/eventlog/logAdapter';

import { CodeState, Mode, useDistributionCode } from '../hooks/useDistributionCode';

const CopiedIcon = (
  <div className="w-[20px] h-[20px] flex rounded-full justify-center items-center bg-[#3857994D]">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
    >
      <path
        d="M2.875 7.67473L6.11244 10.9122L12.5866 4.43726"
        stroke="white"
        strokeWidth="1.17"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </div>
);

interface InviteComponentProps {
  distributionCode: ReturnType<typeof useDistributionCode>;
}

const InviteComponent = ({ distributionCode }: InviteComponentProps) => {
  const { t } = useTranslate();
  const { inviteInfo, isCopied, copyUrl, inviteUrl, loading } = distributionCode;

  const isKOL = Boolean(inviteInfo?.pattern === 2);
  const isDisabled = inviteInfo?.codeState === CodeState.Disable;

  if (!inviteInfo) {
    return null;
  }

  const unit = isCnArea ? '¥' : '$';

  // 格式化分佣值
  const getProceedValue = (): string => {
    switch (inviteInfo.proceedsMode) {
      case Mode.FIXED_AMOUNT:
        return `${unit}${inviteInfo.proceedsAmount ? inviteInfo.proceedsAmount / 100 : '-'}`;
      case Mode.FIXED_RATIO:
        return `${inviteInfo.proceedsRatio ? inviteInfo.proceedsRatio * 100 : '-'}%`;
      default:
        return '';
    }
  };

  // 格式化优惠值
  const getDiscountValue = (): string => {
    switch (inviteInfo.discountMode) {
      case Mode.FIXED_AMOUNT:
        return `${unit}${inviteInfo.discountAmount ? inviteInfo.discountAmount / 100 : '-'}`;
      case Mode.FIXED_RATIO:
        return `${inviteInfo.discount ? ((1000000 - inviteInfo.discount * 1000000) / 1000000) * 100 : '-'}%`;
      default:
        return '';
    }
  };

  const proceedValue = getProceedValue();
  const discountValue = getDiscountValue();

  const jumpHandle = () => {
    // trackEvent('fc_invitepanel_apply_click', {});
    window.open(
      isCnArea
        ? 'https://quvideo.feishu.cn/share/base/form/shrcnSjqcjn7n5DpHiATu1SmR9g'
        : isFWebSite
          ? 'https://docs.google.com/forms/d/1VUEYv8-jsw1XjGaWF-XoDZC_mYvRDtdssd6sBxrb2Ms'
          : 'https://docs.google.com/forms/d/1Qk6PYYdJSDBNeJH5DVJysPI2-AEI8NvAcqxMz_hYjus',
      '_blank',
    );
  };

  const inviteDesc = inviteInfo.isInvited ? (
    <span className="text-app-tertiary">{t('Incentive-Rule-2-Desc-Apply')}</span>
  ) : (
    <FormattedMessage
      id="Incentive-Rule-2-Desc-Applying"
      values={{
        cta: (chunks: React.ReactNode) => (
          <span
            style={{
              color: '#616cf3',
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
            onClick={jumpHandle}
          >
            {chunks}
          </span>
        ),
      }}
    />
  );

  return (
    <div className="px-[30px] pt-[10px] pb-[24px] flex flex-col gap-[20px]">
      {loading ? (
        <div className="w-full h-[227px] relative">
          <Loading />
        </div>
      ) : (
        <>
          {/* 标题和描述 */}
          <div className="flex flex-col gap-[8px]">
            <p className="text-[20px] font-[600] text-app-primary">
              {t(isKOL ? 'Distribution-KOL-Title' : 'Distribution-Title')} 🎉
            </p>
            <p className="text-[12px] leading-[16px] text-app-secondary">
              {isKOL ? (
                <FormattedMessage
                  id="Distribution-KOL-Subtitle"
                  values={{ discount: discountValue, proceed: proceedValue }}
                />
              ) : (
                t('Distribution-Subtitle')
              )}
            </p>
          </div>

          {/* 邀请链接 - 所有用户都显示 */}
          {inviteUrl && (
            <div className="flex flex-col gap-[8px]">
              <div className="flex items-center gap-[11px] h-[40px] w-full">
                <div className="h-[40px] flex items-center flex-1 rounded-[8px] px-[8px] border-2 border-app-accent text-app-tertiary bg-app-input text-[12px]">
                  <div
                    className="line-clamp-1 w-full break-all"
                    title={inviteUrl}
                    style={{
                      display: '-webkit-box',
                      WebkitLineClamp: 1,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      userSelect: isDisabled ? 'none' : 'auto',
                    }}
                  >
                    {inviteUrl}
                  </div>
                </div>
                <Button
                  className="!shrink-0 !px-[26px] !py-[12px] !text-[16px] !font-[600] !h-[40px] !rounded-[8px] !text-white"
                  style={{
                    backgroundColor: isDisabled ? '#ccc' : '#4931fa',
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                  }}
                  disabled={isDisabled}
                  onClick={() => {
                    copyUrl();
                    trackEvent(EventNewID.INVITE_LINK_COPY, {} as Record<string, never>);
                  }}
                >
                  {isCopied ? CopiedIcon : t('fc.operate.copy')}
                </Button>
              </div>
              {isDisabled && (
                <p className="text-[12px] leading-[16px] text-[#ff4d4f] m-0">
                  {t('Distribution-Code-Disabled')}
                </p>
              )}
            </div>
          )}

          {/* How it works */}
          <div className="flex flex-col gap-[8px]">
            <p className="text-[14px] font-[500] leading-[20px] text-app-secondary">
              {t('Incentive-How-It-Works')}
            </p>
            <div className="flex items-center justify-between w-full gap-[8px]">
              {/* Step 1 */}
              <div className="flex gap-[8px] items-center break-all">
                <div className="w-[28px] h-[28px] rounded-full bg-[#6366f1] flex items-center justify-center shrink-0">
                  <span className="text-[12px] text-white">01</span>
                </div>
                <span className="text-[12px] text-app-secondary">
                  {t('Incentive-Step-1')}
                </span>
              </div>

              {/* Arrow */}
              <svg
                width="29"
                height="8"
                viewBox="0 0 29 8"
                fill="none"
                className="shrink-0"
              >
                <path
                  d="M0.5 4H28.5M28.5 4L25 0.5M28.5 4L25 7.5"
                  stroke="rgba(17,17,17,0.2)"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>

              {/* Step 2 */}
              <div className="flex gap-[8px] items-center break-all">
                <div className="w-[28px] h-[28px] rounded-full bg-[#6366f1] flex items-center justify-center shrink-0">
                  <span className="text-[12px] text-white">02</span>
                </div>
                <span className="text-[12px] text-app-secondary">
                  {t(isKOL ? 'Incentive-KOL-Step-2' : 'Incentive-Step-2')}
                </span>
              </div>

              {/* Arrow */}
              <svg
                width="29"
                height="8"
                viewBox="0 0 29 8"
                fill="none"
                className="shrink-0"
              >
                <path
                  d="M0.5 4H28.5M28.5 4L25 0.5M28.5 4L25 7.5"
                  stroke="rgba(17,17,17,0.2)"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>

              {/* Step 3 */}
              <div className="flex gap-[8px] items-center break-all">
                <div className="w-[28px] h-[28px] rounded-full bg-[#6366f1] flex items-center justify-center shrink-0">
                  <span className="text-[12px] text-white">03</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-[12px] leading-[16px] text-app-secondary">
                    {t(isKOL ? 'Incentive-KOL-Step-3-Line1' : 'Incentive-Step-3-Line1')}
                  </span>
                  {!isKOL && (
                    <span className="text-[10px] leading-[14px] text-app-tertiary">
                      {t('Incentive-Step-3-Line2')}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* 底部提示文字 - 只有普通用户显示申请入口 */}
          {inviteInfo?.pattern === 1 && (
            <p className="text-[12px] leading-[16px] text-app-secondary text-center">
              {t('Incentive-Rule-2-Desc-Applying-Prefix')}
              {inviteDesc}
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default InviteComponent;
